import Config from "@via/utils/config";
import Axios from "axios";
import {
  buildLoginUrl,
  isSafeRedirectPath,
  setPostLoginRedirect,
} from "@via/utils/post-login-redirect";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Background reads set this so a 469 (account upgrade needed) rejects
     * quietly instead of opening the Upgrade Your Account dialog. The dialog
     * belongs to actions the user asked for, not to data a page loads on its own.
     */
    skipUpgradeModal?: boolean;
  }
}

// Global variable to store the upgrade modal function
let showUpgradeModal: (() => void) | null = null;

// Function to set the upgrade modal function from the context
export const setUpgradeModalFunction = (fn: () => void): void => {
  showUpgradeModal = fn;
};

// Function to check if we're currently handling an upgrade required error
export const isHandlingUpgradeRequiredError = (): boolean => {
  return isHandlingUpgradeRequired;
};

// Function to reset the upgrade required flag
export const resetUpgradeRequiredFlag = (): void => {
  isHandlingUpgradeRequired = false;
};

const baseURL = Config.ROUTER_BASENAME;

const axios = Axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
  },
  timeout: 120000,
  withCredentials: true,
});

// Global flag to track if we're currently handling an upgrade required error
let isHandlingUpgradeRequired = false;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, success: boolean = true) => {
  failedQueue.forEach(({ resolve, reject }) => {
    success ? resolve() : reject(error);
  });
  failedQueue = [];
};

const logout = async () => {
  try {
    await axios.get("/api/auth/logout");
  } finally {
    const basename = Config.ROUTER_BASENAME || "";
    const isBasenameExactMatch =
      basename && window.location.pathname === basename;
    const isBasenamePrefixed =
      basename && window.location.pathname.startsWith(`${basename}/`);
    const appPathname = isBasenameExactMatch
      ? "/"
      : isBasenamePrefixed
        ? window.location.pathname.slice(basename.length)
        : window.location.pathname;
    const target = appPathname + window.location.search;

    let loginHref = `${basename}/login`;
    if (isSafeRedirectPath(target)) {
      setPostLoginRedirect(target);
      loginHref = `${basename}${buildLoginUrl(target)}`;
    }

    window.location.href = loginHref;
  }
};

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (!originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }

    // If unauthorized and retry count is within limit
    if (error.response?.status === 401 && originalRequest._retryCount < 3) {
      // If refresh is already in progress, queue this request (regardless of visibility)
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      // Skip starting a NEW refresh if tab is in background to prevent concurrent refresh from multiple tabs
      // When user switches back, refetchOnWindowFocus will trigger a fresh API call
      if (document.visibilityState !== "visible") {
        return Promise.reject(error);
      }

      originalRequest._retryCount += 1;
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.get("/api/auth/v1/refresh-token");
        isRefreshing = false;
        processQueue(null, true);
        return axios(originalRequest); // Retry the original request
      } catch (refreshError) {
        isRefreshing = false;
        // A refresh refused with 401, or with 403 (stale, expired, revoked, or
        // past the MFA re-authentication window), means the session is over.
        const refreshStatus = (refreshError as any).response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          await logout();
          return Promise.reject(refreshError);
        }
        processQueue(refreshError, false);
        return Promise.reject(refreshError);
      }
    }

    // If retries exceeded
    if (error.response?.status === 401 && originalRequest._retryCount >= 3) {
      await logout();
      return Promise.reject(error);
    }

    // Handle upgrade required (469 status code)
    if (error.response?.status === 469) {
      if (!originalRequest.skipUpgradeModal) {
        if (showUpgradeModal) {
          showUpgradeModal();
        }
        isHandlingUpgradeRequired = true;
      }
      const upgradeError = new Error("Upgrade Required");
      (upgradeError as any).isUpgradeRequired = true;
      return Promise.reject(upgradeError);
    }

    return Promise.reject(error);
  }
);

// Comment out for now until we fix status codes being returned across the app
/* const setupInterceptors = navigate => {
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response && error.response.status === 404) {
        navigate("/404");
      }
      return Promise.reject(error);
    },
  );
}; */

export { axios };
